/**
 * Audit log — every config, site, origin, member and destination change,
 * newest first, with who did it (a partner user or a FinderlyFix operator).
 * Indefinite retention; nothing here can be deleted.
 *
 * Each entry reads as a sentence ("Published widget settings v2 (test key)",
 * ../audit/catalog.ts), the person as their email or name rather than
 * "user #7", and a lead or key ID a reader may want sits on a quiet
 * secondary line. The raw resource path and HTTP method are in Details only. Details open a
 * field-by-field before/after view, with the raw JSON one more click away.
 * The Action filter is built from the full catalogue (plus anything newer the
 * log itself contains), so an action can never be missing from it.
 *
 * Below 720 px each entry is a card: a table with an ID column squeezed IDs
 * to one or two characters per line at 390 px.
 *
 * `?agent_token=pat_…` (T093) narrows it to one AI agent token's activity —
 * Account → AI agent access links each token here — and the Agent select sets
 * it. The filter lives in the URL so the per-token view can be linked and
 * shared inside the team.
 */
import { Fragment, useEffect, useId, useMemo, useState } from 'react';
import { useSession } from '../auth/session';
import type { AgentToken, AuditEntry } from '../api/types';
import { describeAudit, detailRows, filterGroups, type DescribeContext } from '../audit/catalog';
import { Link, useRouter } from '../router';
import { useSites } from '../state/sites';
import { Empty, ErrorNotice, Loading, PageHead, formatDateTime } from '../ui/kit';
import { useMutation, usePhone, useQuery } from '../ui/hooks';

/** Same shape the server validates; anything else is ignored, not sent. */
const AGENT_TOKEN_ID = /^pat_[0-9A-HJKMNP-TV-Z]{26}$/;

/** The per-token activity view for one agent token. */
export function agentActivityPath(tokenId: string): string {
  return `/audit?agent_token=${encodeURIComponent(tokenId)}`;
}

function tokenOptionLabel(t: AgentToken): string {
  return t.status === 'active' ? t.label : `${t.label} (${t.status})`;
}

/** Who did it, in words: a FinderlyFix operator, the automatic sweep, or a member by email/name. */
export function actorName(e: AuditEntry, member: (id: number) => string | null): string {
  if (e.actor_operator === 'system') return 'FinderlyFix (automatic)';
  if (e.actor_operator === 'contractor') return 'The contractor (their own link)';
  if (e.actor_operator === 'homeowner') return 'The homeowner (from the widget)';
  if (e.actor_operator === 'notice_recipient_link') return 'The notice address (its own link)';
  if (e.actor_operator) return `FinderlyFix operator (${e.actor_operator})`;
  if (e.actor_user_id === null) return 'FinderlyFix';
  return member(e.actor_user_id) ?? `Former member #${e.actor_user_id}`;
}

/**
 * A name or email that may break only after an "@": "maria@" /
 * "cascadehomes.com", never "maria@casc" / "adehomes.com" or at a hyphen in
 * the domain (second audit, 2026-09-27). A part wider than the column is
 * cut with an ellipsis; the full text is the tooltip.
 */
export function BreakAfterAt({ text }: { text: string }) {
  const parts = text.split('@');
  return (
    <span className="break-after-at" title={text}>
      {parts.map((p, i) => (
        <Fragment key={i}>
          <span className="seg">{i < parts.length - 1 ? `${p}@` : p}</span>
          {i < parts.length - 1 ? <wbr /> : null}
        </Fragment>
      ))}
    </span>
  );
}

function Actor({ e, member, agentToken }: { e: AuditEntry; member: (id: number) => string | null; agentToken: string }) {
  return (
    <>
      {e.actor_operator ? <span className="tag info">{actorName(e, member)}</span> : <BreakAfterAt text={actorName(e, member)} />}
      {e.actor_agent_token_id ? (
        <div>
          {e.actor_agent_token_id === agentToken ? (
            <span className="tag accent" title={e.actor_agent_token_id}>
              via agent “{e.actor_agent_token_label ?? e.actor_agent_token_id}”
            </span>
          ) : (
            <Link to={agentActivityPath(e.actor_agent_token_id)} className="tag accent" title={`Everything this agent token did (${e.actor_agent_token_id})`}>
              via agent “{e.actor_agent_token_label ?? e.actor_agent_token_id}”
            </Link>
          )}
        </div>
      ) : null}
    </>
  );
}

/** The before/after of one entry as rows, with the raw JSON behind a toggle. */
export function AuditDetails({ e, id }: { e: AuditEntry; id: string }) {
  const [raw, setRaw] = useState(false);
  const rows = detailRows(e);
  const both = Boolean(e.before && e.after);
  return (
    <div id={id} className="audit-details stack-sm">
      {rows.length ? (
        <table className="kv">
          <caption className="visually-hidden">What changed</caption>
          <thead>
            <tr>
              <th scope="col">Field</th>
              {both || e.before ? <th scope="col">{both ? 'Before' : 'Value'}</th> : null}
              {e.after ? <th scope="col">{both ? 'After' : 'Value'}</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.field} className={both && r.before !== r.after ? 'changed' : undefined}>
                <th scope="row">{r.field}</th>
                {both || e.before ? <td>{r.before ?? '—'}</td> : null}
                {e.after ? <td>{r.after ?? '—'}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="small muted">No details were recorded.</p>
      )}
      <div className="small faint wrap-anywhere">
        Resource <span className="mono">{e.resource}</span>
      </div>
      <div>
        <button type="button" className="btn btn-sm btn-ghost" aria-expanded={raw} onClick={() => setRaw(!raw)}>
          {raw ? 'Hide raw JSON' : 'Show raw JSON'}
        </button>
      </div>
      {raw ? (
        <pre className="mono small audit-raw" tabIndex={0} aria-label="Raw JSON">
          {JSON.stringify({ before: e.before, after: e.after }, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function DetailsToggle({ open, onToggle, controls, label }: { open: boolean; onToggle: () => void; controls: string; label: string }) {
  return (
    <button type="button" className="btn btn-sm" aria-expanded={open} aria-controls={open ? controls : undefined} aria-label={`${open ? 'Hide' : 'Show'} details: ${label}`} onClick={onToggle}>
      {open ? 'Hide' : 'Details'}
    </button>
  );
}

export function Audit() {
  const { api } = useSession();
  const { pairs } = useSites();
  const phone = usePhone();
  const baseId = useId();
  const { search, navigate } = useRouter();
  const rawToken = search.get('agent_token') ?? '';
  const agentToken = AGENT_TOKEN_ID.test(rawToken) ? rawToken : '';
  const [action, setAction] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [extra, setExtra] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const filter = { action: action || undefined, agent_token: agentToken || undefined };
  const first = useQuery(() => api.audit(filter), [action, agentToken]);
  // Owners and admins (the only roles here) can list every token in the org.
  const tokens = useQuery(() => api.listAgentTokens(), []);
  // Names for "By". Owners and admins (the only roles here) can list members;
  // a failed read only costs the names.
  const members = useQuery(() => api.listMembers(), []);
  useEffect(() => {
    setExtra([]);
    setCursor(first.data?.next_cursor ?? null);
  }, [first.data]);
  const more = useMutation(async () => {
    if (!cursor) return;
    const res = await api.audit(filter, cursor);
    setExtra((x) => [...x, ...res.items]);
    setCursor(res.next_cursor);
  });
  const items = [...(first.data?.items ?? []), ...extra];
  const tokenList = tokens.data?.items ?? [];
  const selected = tokenList.find((t) => t.id === agentToken);
  const selectedLabel = selected?.label ?? items.find((e) => e.actor_agent_token_id === agentToken)?.actor_agent_token_label ?? agentToken;
  const setAgentToken = (id: string) => navigate(id ? agentActivityPath(id) : '/audit', { replace: true });

  const ctx = useMemo<DescribeContext & { member: (id: number) => string | null }>(() => {
    const byId = new Map<number, string>();
    // A removed member is no longer listed; their invite row still names them.
    for (const e of items) {
      if (e.action === 'member.invite' && e.after && typeof e.after.email === 'string') byId.set(Number(e.after.user_id), e.after.email);
    }
    for (const m of members.data?.items ?? []) byId.set(m.user_id, m.name ? `${m.name} (${m.email})` : m.email);
    const sites = new Map<string, string>();
    // Name the site only when there is more than one; "(test key)" says enough otherwise.
    if (pairs.length > 1) {
      for (const p of pairs) {
        sites.set(p.live.site_key, p.live.label);
        if (p.test) sites.set(p.test.site_key, p.live.label);
      }
    }
    return { member: (id) => byId.get(id) ?? null, siteLabel: (key) => sites.get(key) ?? null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members.data, pairs, first.data, extra]);

  const groups = filterGroups(items.map((e) => e.action).concat(action ? [action] : []));
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <PageHead
        title="Audit log"
        lede="Who changed what, and when. Kept indefinitely."
        actions={
          <>
            <label className="row small" style={{ gap: 6 }} htmlFor="audit-filter-action">
              <span className="label">Action</span>
              <select id="audit-filter-action" className="input" style={{ minHeight: 32, width: 'auto', maxWidth: '100%' }} value={action} onChange={(e) => setAction(e.target.value)}>
                <option value="">All actions</option>
                {groups.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.actions.map((a) => (
                      <option key={a.action} value={a.action}>
                        {a.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            {tokenList.length || agentToken ? (
              <label className="row small" style={{ gap: 6 }} htmlFor="audit-filter-agent">
                <span className="label">Agent</span>
                <select id="audit-filter-agent" className="input" style={{ minHeight: 32, width: 'auto', maxWidth: 240 }} value={agentToken} onChange={(e) => setAgentToken(e.target.value)}>
                  <option value="">Anyone</option>
                  {agentToken && !selected ? <option value={agentToken}>{selectedLabel}</option> : null}
                  {tokenList.map((t) => (
                    <option key={t.id} value={t.id}>
                      {tokenOptionLabel(t)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </>
        }
      />
      {agentToken ? (
        <p className="small muted" role="status" style={{ margin: '0 0 var(--s3)' }}>
          Showing what the agent token “{selectedLabel}” did{selected ? <> (<code>{selected.token_prefix}</code>)</> : null}. Each entry also names the person
          who created the token.{' '}
          <button type="button" className="btn btn-sm" onClick={() => setAgentToken('')}>
            Show everyone
          </button>
        </p>
      ) : null}
      <section className="panel">
        {first.loading && !first.data ? (
          <Loading />
        ) : first.error ? (
          <div className="panel-body">
            <ErrorNotice error={first.error} />
          </div>
        ) : items.length ? (
          phone ? (
            <ol className="cards audit-cards" aria-label="Audit log">
              {items.map((e) => {
                const d = describeAudit(e, ctx);
                const detailsId = `${baseId}-d-${e.id}`;
                const isOpen = open.has(e.id);
                return (
                  <li className="card" key={e.id} data-audit-action={e.action}>
                    <strong style={{ color: 'var(--ink)' }}>{d.sentence}</strong>
                    <span className="small muted">
                      {formatDateTime(e.created_at)} · <Actor e={e} member={ctx.member} agentToken={agentToken} />
                    </span>
                    {d.ids.length ? <span className="small faint mono wrap-anywhere">{d.ids.join(' · ')}</span> : null}
                    <span className="card-actions">
                      <DetailsToggle open={isOpen} onToggle={() => toggle(e.id)} controls={detailsId} label={d.sentence} />
                    </span>
                    {isOpen ? <AuditDetails e={e} id={detailsId} /> : null}
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="table-wrap">
              <table className="data audit-table">
                <caption className="visually-hidden">Audit log</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">What happened</th>
                    <th scope="col">By</th>
                    <th scope="col">
                      <span className="visually-hidden">Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => {
                    const d = describeAudit(e, ctx);
                    const detailsId = `${baseId}-d-${e.id}`;
                    const isOpen = open.has(e.id);
                    return (
                      <Fragment key={e.id}>
                        <tr data-audit-action={e.action}>
                          <td className="small muted" style={{ whiteSpace: 'nowrap' }}>
                            {formatDateTime(e.created_at)}
                          </td>
                          <td>
                            <div style={{ color: 'var(--ink)', fontWeight: 600 }}>{d.sentence}</div>
                            {d.ids.length ? <div className="small faint mono wrap-anywhere">{d.ids.join(' · ')}</div> : null}
                          </td>
                          <td className="small">
                            <Actor e={e} member={ctx.member} agentToken={agentToken} />
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <DetailsToggle open={isOpen} onToggle={() => toggle(e.id)} controls={detailsId} label={d.sentence} />
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr className="audit-detail-row">
                            <td colSpan={4}>
                              <AuditDetails e={e} id={detailsId} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <Empty title={agentToken ? 'This agent token has not done anything yet' : action ? 'Nothing logged for this action' : 'Nothing logged yet'} />
        )}
        {cursor ? (
          <div className="panel-body" style={{ textAlign: 'center' }}>
            <button type="button" className="btn" disabled={more.pending} onClick={() => void more.run()}>
              Load older
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
